import { AppEnvAdapter } from '../adapters/app-env.adapter';
import { BrowserGlobalAdapter } from '../adapters/browser-global.adapter';
import { ViteEnvAdapter } from '../adapters/vite-env.adapter';
import { AppConfigValueMissingError } from '../errors/app-config.errors';
import type {
	AppConfig,
	AppConfigMap,
	AppConfigNamespace,
	CreatorAppConfig,
	PortalAppConfig,
	SharedAppConfig,
	VibeBuilderAppConfig
} from '../types/app-config.types';
import type { AppConfigSource } from '../types/app-config-source.types';
import { mergeAppConfig } from '../utils/merge-app-config.util';
import { pickAppConfig } from '../utils/pick-app-config.util';

class AppConfigService {
	private config: AppConfig = {};
	private isRuntimeInitialized = false;
	private readonly sources: AppConfigSource[] = [
		new ViteEnvAdapter(),
		new AppEnvAdapter(),
		new BrowserGlobalAdapter()
	];

	public initializeFromRuntime(): void {
		if (this.isRuntimeInitialized) {
			return;
		}

		const runtimeConfig = this.sources.reduce<AppConfig>(
			(aggregatedConfig, source) =>
				mergeAppConfig(aggregatedConfig, source.read()),
			{}
		);

		// Preserve overrides that may have been configured by the host earlier.
		this.config = mergeAppConfig(runtimeConfig, this.config);
		this.isRuntimeInitialized = true;
	}

	public configure(config: AppConfig): void {
		this.config = mergeAppConfig(this.config, config);
	}

	public reset(): void {
		this.config = {};
		this.isRuntimeInitialized = false;
	}

	public getConfig(): AppConfig {
		this.initializeFromRuntime();

		return this.config;
	}

	public getSharedConfig(): Partial<SharedAppConfig> {
		return this.getAppConfig('shared');
	}

	public getPortalConfig(): Partial<PortalAppConfig> {
		return this.getAppConfig('portal');
	}

	public getCreatorConfig(): Partial<CreatorAppConfig> {
		return this.getAppConfig('creator');
	}

	public getVibeBuilderConfig(): Partial<VibeBuilderAppConfig> {
		return this.getAppConfig('vibeBuilder');
	}

	public getAppConfig<TNamespace extends AppConfigNamespace>(
		namespace: TNamespace
	): Partial<AppConfigMap[TNamespace]> {
		return pickAppConfig(this.getConfig(), namespace);
	}

	public getRequiredValue<
		TNamespace extends AppConfigNamespace,
		TKey extends keyof AppConfigMap[TNamespace]
	>(
		namespace: TNamespace,
		key: TKey
	): NonNullable<AppConfigMap[TNamespace][TKey]> {
		const config = this.getAppConfig(namespace);
		const value = config[key];

		if (value == null) {
			throw new AppConfigValueMissingError(`${namespace}.${String(key)}`);
		}

		return value as NonNullable<AppConfigMap[TNamespace][TKey]>;
	}
}

export const appConfig = new AppConfigService();
